import { useAuthStep } from "@/hooks/useAuthStep";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { cn, trackEvent } from "@/lib/utils";
import { Check } from "lucide-react";
import { motion } from "framer-motion";

const REASON_OPTIONS = ["Theming", "Time Management", "Organization", "Studying"] as const;

const Questionairre = () => {

    const { setAuthStep, school, uuid, token, ageBand } = useAuthStep();

    const [waiting, setWaiting] = useState<boolean>(false);

    const [studentType, setStudentType] = useState<string>("");
    const [schoolName, setSchoolName] = useState<string>(school || "");
    const [graduationYear, setGraduationYear] = useState<string>("");
    const [reference, setReference] = useState<string>("");
    const [reasons, setReasons] = useState<string[]>([]);

    const toggleReason = (reason: string) => {
        setReasons(prev => prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]);
    }
    const [validationErrors, setValidationErrors] = useState<{
        studentType: boolean;
        schoolName: boolean;
        graduationYear: boolean;
        reference: boolean;
    }>({
        studentType: false,
        schoolName: false,
        graduationYear: false,
        reference: false
    });

    const handleSubmit = async () => {
        try {
            await trackEvent("finished_questionairre", { "distinct_id": uuid, "origin": school, "student_type": studentType, "graduation_year": graduationYear, "reference": reference, "reasons": reasons });
        } catch (e) {
        }
        setAuthStep("questionairre3");
    }

    return (
        <div className="px-[30px] mb-[30px] flex flex-col h-full h-full text-gray-500">
            <div>
                <h1 className="text-2xl font-semibold text-black mb-[5px]">Welcome to BetterCampus!</h1>
                <p>Real quick - tell us a bit about you</p>
            </div>
            <div className="flex flex-col gap-[10px] mt-[10px]">
                {/*}
                <div>
                    <p className="text-sm font-medium text-black mb-[5px]">What school do you go to?</p>
                    <Select
                        value={school || schoolName}
                        onValueChange={(value) => {
                            setSchoolName(value);
                            setValidationErrors(prev => ({ ...prev, schoolName: false }));
                        }}
                        disabled={!!school}
                    >
                        <SelectTrigger className={`${validationErrors.schoolName ? "ring-2 ring-red-500 ring-opacity-50 shadow-lg shadow-red-500/25" : ""}`}>
                            <SelectValue placeholder={"Select"} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={school}>{school}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                */}
                <div>
                    <p className="text-sm font-medium text-black mb-[5px]">What type of student are you?</p>
                    <Select
                        value={studentType}
                        onValueChange={(value) => {
                            setStudentType(value);
                            setValidationErrors(prev => ({ ...prev, studentType: false }));
                        }}
                    >
                        <SelectTrigger className={`${validationErrors.studentType ? "ring-2 ring-red-500 ring-opacity-50 shadow-lg shadow-red-500/25" : ""}`}>
                            <SelectValue placeholder={"Select"} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="middle">Middle school</SelectItem>
                            <SelectItem value="high">High School</SelectItem>
                            <SelectItem value="university">Undergraduate / University</SelectItem>
                            <SelectItem value="instructor">Instructor</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <p className="text-sm font-medium text-black mb-[5px]">What year do you graduate?</p>
                    <Select
                        value={graduationYear}
                        onValueChange={(value) => {
                            setGraduationYear(value);
                            setValidationErrors(prev => ({ ...prev, graduationYear: false }));
                        }}
                    >
                        <SelectTrigger className={`${validationErrors.graduationYear ? "ring-2 ring-red-500 ring-opacity-50 shadow-lg shadow-red-500/25" : ""}`}>
                            <SelectValue placeholder={"Select"} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="2026">2026</SelectItem>
                            <SelectItem value="2027">2027</SelectItem>
                            <SelectItem value="2028">2028</SelectItem>
                            <SelectItem value="2029">2029</SelectItem>
                            <SelectItem value="2030">2030</SelectItem>
                            <SelectItem value="2031">2031</SelectItem>
                            <SelectItem value="2032">2032+</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <p className="text-sm font-medium text-black mb-[5px]">Where did you first hear about us?</p>
                    <Select
                        value={reference}
                        onValueChange={(value) => {
                            setReference(value);
                            setValidationErrors(prev => ({ ...prev, reference: false }));
                        }}
                    >
                        <SelectTrigger className={`${validationErrors.reference ? "ring-2 ring-red-500 ring-opacity-50 shadow-lg shadow-red-500/25" : ""}`}>
                            <SelectValue placeholder={"Select"} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="saw">Saw someone using it</SelectItem>
                            <SelectItem value="friend">Friend showed me</SelectItem>
                            <SelectItem value="tiktok">TikTok</SelectItem>
                            <SelectItem value="instagram">Instagram</SelectItem>
                            <SelectItem value="twitter">Twitter/X</SelectItem>
                            <SelectItem value="youtube">YouTube</SelectItem>
                            <SelectItem value="school">School/teacher</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <p className="text-sm font-medium text-black mb-[5px]">How can BetterCanvas help you?</p>
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                        {REASON_OPTIONS.map((reason) => {
                            const isSelected = reasons.includes(reason);
                            return (
                                <button
                                    key={reason}
                                    onClick={() => toggleReason(reason)}
                                    className={cn(
                                        "relative flex items-center justify-center py-1.5 rounded-full border text-xs font-medium transition-all duration-200 pl-2.5 pr-7",
                                        isSelected
                                            ? "bg-[#ff7a5c] text-white border-[#ff7a5c]"
                                            : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                                    )}
                                >
                                    <span className="inset-0 flex items-center justify-center opacity-0 pointer-events-none">
                                        {reason}
                                    </span>
                                    <motion.span
                                        className="absolute  z-1 inset-0 flex items-center justify-center"
                                        initial={{ x: 0 }}
                                        animate={{ x: isSelected ? -8 : 0 }}
                                        transition={{ duration: 0.1 }}
                                    >
                                        {reason}
                                    </motion.span>
                                    <span className="absolute right-2 w-3 h-3 flex items-center justify-center">
                                        {isSelected && <Check className="w-3 h-3" />}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="flex-1 flex flex-col justify-end gap-[10px] mt-4">
                <button style={{ background: "linear-gradient(90deg, #ffa952,#ff686c)" }} className="flex items-center justify-center gap-[8px] py-[12px] rounded-full w-full font-medium text-white" onClick={handleSubmit}>Next 1/2</button>
            </div>
        </div>
    );
};

export default Questionairre;
